import { get, isEmpty, map } from "lodash";
import {
  Notification,
  Avatar,
  Flex,
  FlexItem,
  StyledLink,
  Box,
  Divider,
  Badge,
} from "orcs-design-system";
import PropTypes from "prop-types";
import React from "react";

import GroupTypeBadge from "src/components/Badge/GroupTypeBadge";
import GroupHierarchyBreadcrumb from "src/components/GroupHierarchyBreadcrumb";
import AvatarPlaceholder from "src/components/Placeholder/AvatarPlaceholder";
import GroupPropType from "src/custom-prop-types/group";
import { GroupTypesPropType } from "src/custom-prop-types/groupTypes";
import { ADD_ENTITY_TO_TEAM_SUCCESS_MESSAGE } from "src/consts/tags";
import TagBadge from "src/components/Badge/TagBadge";
import {
  getGroupBgImg,
  isGroupVisible,
  getGroupExternalId,
} from "src/util/group";
import { getTeamGradient, AvatarWithGradient } from "src/util/gradient";
import URL, { url } from "src/consts/urlPaths";
import MemberCountQueryBadge from "src/components/MemberCountQueryBadge";
import TagDisplay from "src/components/TagDisplay";
import { ENTITY_TYPES } from "src/consts/entityTypes";
import { useOrgBranding } from "src/hooks/orgConfig/hooks/useOrgBranding";
import { getParsedCropDataFromAttributes } from "src/commonComponents/TeamProfileHeader/utils/getParsedCropDataFromAttributes";
import PersistedMoveInfo from "src/components/PersistedMoveInfo";

const TeamListItem = ({
  group,
  loading,
  groupTypes,
  isLastItem,
  added = false,
  tags,
  showInPage = false,
  actionItem: ActionItem,
  cardView,
  successNotificationMessage = ADD_ENTITY_TO_TEAM_SUCCESS_MESSAGE,
  renderFteBadge,
  renderPrincipleStatus,
  workspace,
  showAssociationTagBadges = false,
  selectedString,
}) => {
  const { branding } = useOrgBranding();
  const groupId = get(group, "id");
  const bgImg = getGroupBgImg(group);
  const bgGradient = getTeamGradient(branding, group);

  if (!groupId || (!loading && !isGroupVisible(group))) {
    return null;
  }

  const hasHierarchy = !!get(group, "hierarchy");
  const workspaceFeatureFlags = get(workspace, "config.featureFlags", {});

  const enableAllTagsToBeVisible =
    workspaceFeatureFlags?.enableAllTagsToBeVisible;

  const moveGroupEnhancedEnabled = workspaceFeatureFlags?.enableEnhancedMove;

  const hasPersistedMove = !isEmpty(group?.propertyOverrides);
  const showPersistedMoveInfo = moveGroupEnhancedEnabled && hasPersistedMove;

  const externalId = getGroupExternalId(group);
  const showExternalIdBadge =
    workspaceFeatureFlags?.showTeamExternalIdAsBadge && externalId;

  let nameLink;

  if (hasHierarchy) {
    nameLink = showInPage ? (
      <GroupHierarchyBreadcrumb
        group={group}
        workspace={workspace}
        selectedString={selectedString}
      />
    ) : (
      <GroupHierarchyBreadcrumb group={group} small={false}>
        <StyledLink
          aria-label={`Navigate to ${group.name}`}
          to={url(URL.TEAM_DETAILS_BASE_PATH, { id: groupId })}
          fontSize="3"
        >
          <span>{group.name}</span>
        </StyledLink>
      </GroupHierarchyBreadcrumb>
    );
  } else {
    nameLink = (
      <StyledLink
        aria-label={`Navigate to ${group.name}`}
        to={url(URL.TEAM_DETAILS_BASE_PATH, { id: groupId })}
      >
        <span>{group.name}</span>
      </StyledLink>
    );
  }

  const groupName = groupId && (
    <Flex alignItems="center" gap="4px" flexWrap="wrap">
      {nameLink}
      {showPersistedMoveInfo && (
        <PersistedMoveInfo
          testId={`cp-team-list-item-persisted-move-${groupId}`}
          group={group}
        />
      )}
    </Flex>
  );

  let itemDivider = null;
  if (!isLastItem) {
    itemDivider = <Divider light />;
  }

  const hasAction = !!ActionItem;

  let boxPadding = "s";
  if (cardView) {
    boxPadding = " 0";
  }

  const shouldShowTags = !showAssociationTagBadges && !isEmpty(tags);

  const teamBadges = (
    <>
      <Flex flexWrap="wrap" alignItems="center" role="list">
        <FlexItem mr="xs" mt="xs" flex="0 1 auto" role="listitem">
          <GroupTypeBadge
            group={group}
            groupTypes={groupTypes}
            selectedString={selectedString}
          />
        </FlexItem>
        {showExternalIdBadge && (
          <Badge mr="xs" mt="xs" role="listitem">
            {externalId}
          </Badge>
        )}
        <Box mr="xs" mt="xs" role="listitem">
          <MemberCountQueryBadge
            groupId={group.id}
            isTotalMemberCount
            showToolTip
            hideWhenLoading
          />
        </Box>
        {renderFteBadge && (
          <Box mr="xs" mt="xs" role="listitem">
            {renderFteBadge(group)}
          </Box>
        )}
      </Flex>
      {shouldShowTags && (
        <TagDisplay
          tags={tags}
          entityType={ENTITY_TYPES.GROUP}
          showHighlightedTagsOnly={!enableAllTagsToBeVisible}
        />
      )}
      {showAssociationTagBadges && !isEmpty(tags) && (
        <Flex flexWrap="wrap" mt="xs" role="list">
          {map(tags, (tag) => {
            return (
              <TagBadge
                key={tag.id}
                value={tag}
                withType={true}
                getTypeName={() => get(tag, "tagTypeMetadata.name", tag.type)}
                disabled
              />
            );
          })}
        </Flex>
      )}
      {renderPrincipleStatus && (
        <Box mt="s">{renderPrincipleStatus(group)}</Box>
      )}
    </>
  );

  const AvatarComponent = bgImg ? Avatar : AvatarWithGradient;

  return loading ? (
    <AvatarPlaceholder rows={1} />
  ) : (
    <>
      <Box py={boxPadding} px={cardView ? "0" : ""}>
        {added && (
          <Notification mb="r" variant="success" icon="check-circle">
            {successNotificationMessage}
          </Notification>
        )}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          flexWrap="nowrap"
        >
          <FlexItem flex="1 1 auto">
            <AvatarComponent
              data-testid="cp-explore-team-list-item-panel"
              shape="square"
              sizing={cardView ? "large" : "small"}
              title={groupName}
              image={bgImg}
              imageAlt={`Avatar of ${group.name}`}
              bgGradient={bgImg ? undefined : bgGradient}
              type="default"
              subtitleContent={teamBadges}
              croppedAreaPercent={getParsedCropDataFromAttributes(
                group?.attributes
              )}
            />
          </FlexItem>
          {hasAction && (
            <FlexItem flex="0 0 auto">
              <ActionItem team={group} tags={tags} />
            </FlexItem>
          )}
        </Flex>
      </Box>
      {cardView ? null : itemDivider}
    </>
  );
};

TeamListItem.propTypes = {
  group: PropTypes.oneOfType([GroupPropType, PropTypes.number]), // number for placeholder
  groupTypes: GroupTypesPropType,
  loading: PropTypes.bool,
  isLastItem: PropTypes.bool,
  showInPage: PropTypes.bool,
  cardView: PropTypes.bool,
  added: PropTypes.bool,
  tags: PropTypes.array,
  actionItem: PropTypes.elementType,
  successNotificationMessage: PropTypes.string,
  renderFteBadge: PropTypes.func,
  renderPrincipleStatus: PropTypes.func,
  workspace: PropTypes.object,
  showAssociationTagBadges: PropTypes.bool,
  selectedString: PropTypes.string,
};

export default TeamListItem;
